import { MeepOrderRepository } from "Infrastructure/repositories/api/OrderRepository";
import { useAppInsights } from "Infrastructure/repositories/appInsights/AppInsights";
import { getOrderRepository } from "Infrastructure/repositories/localStorage/getOrderRepository";
import { useKiosk } from "application/contexts/kiosk/KioskContext";
import {
  PrinterStore,
  usePrinterStore,
} from "application/contexts/printer/PrinterStore";
import { useSession } from "application/contexts/session/SessionContext";
import { useUi } from "application/contexts/ui/UIContext";
import { ErrorCodeType, IError } from "application/models/IError";
import { IActionModalHandler } from "components/actionModal/ActionModal";
import { Kiosk } from "domains/kiosk/aggregates/Kiosk";
import { NfceService } from "domains/order/services/NfceService";
import { ICartContext } from "modules/cart/domain/interfaces/IUseCaseCart";
import { useCartStore } from "modules/cart/infra/store/CartStore";
import {
  PixApprovedTransaction,
  PixPendingTransaction,
} from "modules/payment/domain/models/PixApprovedTransaction";
import {
  IPaymentRef,
  IPaymentResult,
} from "modules/payment/presentation/PaymentProvider";
import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useHistory } from "react-router-dom";
import { GetError } from "utils/GetError";
import { useShallow } from "zustand/react/shallow";
import { CreatePrintNfceUseCase } from "../application/useCases/CreatePrintNfceUseCase";
import { CreatePrintOrderUseCase } from "../application/useCases/CreatePrintOrderUseCase";
import { CreatePrintReceiptUseCase } from "../application/useCases/CreatePrintReceiptUseCase";
import { CreatePrintRemoteUseCase } from "../application/useCases/CreatePrintRemoteUseCase";
import { EmitNfceUseCase } from "../application/useCases/EmitNfceUseCase";
import { GetPasswordUseCase } from "../application/useCases/GetPasswordUseCase";
import {
  ISyncOrderResult,
  SyncOrderUseCase,
} from "../application/useCases/SyncOrderUseCase";
import { IEmitedNfce } from "../domain/models/INfce";
import { IOrder } from "../domain/models/IOrder";
import { IOrderLog } from "../domain/models/IOrderLog";
import { IPedidoPosDTO, StatusPedido } from "../domain/models/IPedidoPosDTO";
import {
  IFinancialTransactionModel,
  Payment,
  TransactionType,
} from "../domain/models/Payment";
import { IOrderProviderRef } from "./OrderProvider";
import { ICustomerIdentificationHandler } from "./customerIdentification/CustomerIdentification";
import { CashlessRepository } from "Infrastructure/repositories/api/CashlessRepository";
import { IPasswordPainel } from "Infrastructure/interfaces/IPasswordPainel";
import { useCardReader } from "application/contexts/cardReader/CardReaderContext";
import { validateCNPJ, validateCPF } from "utils/utils";
import { HttpRepository } from "Infrastructure/repositories/api/_HttpRepository";
import UserRegisterService from "modules/cpfRegister/service/UserRegisterService";
import { GetDocumentOwnerUseCase } from "../application/useCases/GetDocumentOwnerUseCase";
import { useTranslation } from "i18n/useTranslation";
import { ICustomerDiscountsHandler } from "./customerDiscounts/CustomerDiscounts";
import { useValidateInventory } from "./useValidateInventory";

export type RechargeType =
  | "Recharge"
  | "Payment"
  | "WalletRecharge"
  | undefined;

const locaStorageOrderRepository = getOrderRepository();
const nfceService = NfceService();
const cashlessRepository = CashlessRepository();

const UseOrderProvider = (
  orderProviderRef: React.ForwardedRef<IOrderProviderRef>,
  printReceiptRef: React.RefObject<IActionModalHandler>,
  printNfceRef: React.RefObject<IActionModalHandler>,
  customerRef: React.RefObject<ICustomerIdentificationHandler>,
  customerDiscountsRef: React.RefObject<ICustomerDiscountsHandler>
) => {
  const paymentProvider = useRef<IPaymentRef>(null);
  const countPaymentVoucher = useRef(0);
  const paymentReceipt = useRef<Payment>();
  const _order = useRef<IOrder>();

  const [addOrdersToPrintQueue] = usePrinterStore(
    useShallow((state: PrinterStore) => [state.addOrders])
  );

  const [clearCart] = useCartStore((state: ICartContext) => [state.clearCart]);
  const { sessaoIdPos } = useSession();
  const { toastFullscreen, toast, showLoading, hideLoading } = useUi();
  const { kiosk } = useKiosk();
  const { replace } = useHistory();
  const { addLog } = useAppInsights();
  const { cardData, isNominalData } = useCardReader();
  const orderRepository = MeepOrderRepository(addLog);
  const { t } = useTranslation();
  const { validateInventory } = useValidateInventory();

  const [rechargePayment, setRechargePayment] =
    useState<RechargeType>(undefined);
  const [documentToNf, setDocumentToNf] = useState("");
  const [includDocument, setIncludDocument] = useState(false);
  const [docError, setDocError] = useState("");
  const [enableNfAction, setEnableNfAction] = useState(false);
  const [documentOwnerName, setDocumentOwnerName] = useState("");
  const [documentLookupLoading, setDocumentLookupLoading] = useState(false);

  const docToNf = useRef<string | null>(null);

  useEffect(() => {
    let active = true;

    const validateAndFetchDocument = async () => {
      setDocumentOwnerName("");
      setDocError("");
      setEnableNfAction(false);

      if (!includDocument) {
        docToNf.current = null;
        setEnableNfAction(true);
        return;
      }

      const cleanedDocument = documentToNf.replace(/\D/g, "");

      if (cleanedDocument.length === 11) {
        if (!validateCPF(cleanedDocument)) {
          setDocError(t("input_cpf_cnpj.error.cpf_invalid"));
          return;
        }
      } else if (cleanedDocument.length === 14) {
        if (!validateCNPJ(cleanedDocument)) {
          setDocError(t("input_cpf_cnpj.error.cnpj_invalid"));
          return;
        }
      } else {
        return;
      }

      docToNf.current = cleanedDocument;

      if (!kiosk?.localId) {
        return;
      }

      setDocumentLookupLoading(true);

      try {
        addLog("order", { message: `Validando documento ${cleanedDocument}` });
        const httpRepository = HttpRepository();
        const userRegisterService = UserRegisterService(httpRepository);
        const documentOwner = await GetDocumentOwnerUseCase(
          userRegisterService,
          cleanedDocument,
          kiosk.localId
        );

        addLog("order", {
          message: `Documento validado ${cleanedDocument}`,
          documentOwner,
        });

        if (!active) {
          return;
        }

        if (documentOwner?.name) {
          setDocumentOwnerName(documentOwner.name.toLowerCase());
        }

        setEnableNfAction(true);
        setDocError("");
      } catch (error) {
        addLog("order", {
          message: `Erro ao validar documento ${cleanedDocument}`,
          error,
        });

        if (!active) {
          return;
        }
        setDocError(t("order.nfce_modal.confirm_document_failed"));
        setEnableNfAction(false);
      } finally {
        if (active) {
          setDocumentLookupLoading(false);
        }
      }
    };

    validateAndFetchDocument();

    return () => {
      active = false;
    };
  }, [documentToNf, includDocument, kiosk, t]);

  const onpressKeyboard = useCallback((key: string) => {
    setDocumentToNf((prev) => prev + key);
  }, []);

  const onPressBack = useCallback(() => {
    setDocumentToNf((prev) => prev.substring(0, prev.length - 1));
  }, []);

  const addOrderLog = useCallback(
    async (
      order: IOrderLog | null,
      message: string,
      pedidoPOS?: IPedidoPosDTO,
      payment?: Payment,
      nfce?: IEmitedNfce[]
    ) => {
      try {
        if (order) {
          console.log("addOrderLog: order", order, "message", message);
          let orderLog = {
            ...order,
            id: order.id,
            friendlyId: order.friendlyId,
            cartDate: order.cartDate,
            payment: payment ?? order.payment,
            pedidoPOS: pedidoPOS ?? order.pedidoPOS,
            nfce: nfce ?? order.nfce,
            printOrders: order.printOrders,
            ticketId: order.ticketId,
            printType: order.printType,
            orderItems: order.orderItems,
            totalValue: order.totalValue,
          };
          if (message.includes("Pagamento realizado")) {
            const log = {
              id: order.id,
              friendlyId: order.friendlyId,
              cartDate: order.cartDate,
              payment: order.payment,
              totalWithoutDiscount: order.totalWithoutDiscount,
              totalValue: order.totalValue,
              createdAt: order.createdAt,
              passwordPanelCode: order.passwordPanelCode,
              customer: order.customer,
              paymentPix: order.paymentPix,
              groupDiscountOrder: order.groupDiscountOrder,
            };
            addLog("order", { message, log });
          } else {
            addLog("order", { message, orderLog });
          }
          await locaStorageOrderRepository.add(orderLog, message);
        }
      } catch (error) {
        GetError(
          error,
          "addOrderLog",
          "OrderProvider",
          "Falha ao adicionar log",
          ErrorCodeType.GENERAL
        );
      }
    },
    [addLog]
  );

  const syncOrder = useCallback(
    async (
      order: IOrder,
      transactionType: TransactionType,
      kiosk: Kiosk,
      recharge?: RechargeType
    ) => {
      try {
        showLoading();
        const syncResult = await SyncOrderUseCase(
          orderRepository,
          cashlessRepository,
          order,
          transactionType,
          kiosk,
          recharge
        );
        await addOrderLog(order, "Pedido enviado", syncResult.pedidoPOS);
        if (!order.isOfflineRecharge) {
          orderRepository.syncOrdersAsync(kiosk.localId, kiosk.id);
        }
        return syncResult;
      } catch (error) {
        GetError(
          error,
          "syncOrder",
          "UseOrderProvider",
          "Falha ao sincronizar pedido",
          ErrorCodeType.GENERAL
        );
      }
    },
    [addOrderLog, orderRepository, showLoading]
  );

  const printPaymentReceipt = useCallback(async () => {
    countPaymentVoucher.current = countPaymentVoucher.current + 1;
    try {
      if (paymentReceipt.current) {
        if (countPaymentVoucher.current <= 1) {
          addOrdersToPrintQueue(
            CreatePrintReceiptUseCase(paymentReceipt.current)
          );
          countPaymentVoucher.current = 0;
        }
      }
    } catch (error) {
      GetError(
        error,
        "printPaymentReceipt",
        "UseOrderProvider",
        "Falha ao imprimir comprovante",
        ErrorCodeType.GENERAL
      );
    }
  }, [addOrdersToPrintQueue]);

  const handleReceiptPrintModal = useCallback(
    async (kiosk: Kiosk, transactionType: TransactionType) => {
      if (kiosk.printReceipt) {
        if (transactionType !== TransactionType.pospaid) {
          try {
            hideLoading();
            await printReceiptRef.current?.open();
            printPaymentReceipt();
          } catch (error) {
            GetError(
              error,
              "printTicket",
              "UseOrderProvider",
              `${(error as any)?.message ?? "Tempo expirado"}`,
              ErrorCodeType.GENERAL
            );
          }
        }
      }
    },
    [hideLoading, printPaymentReceipt, printReceiptRef]
  );

  const emitNfce = useCallback(
    async (order: IOrder, document?: string) => {
      try {
        addOrderLog(order, "Emitir NF");
        await orderRepository.syncOrdersAsync(kiosk?.localId ?? "", kiosk?.id ?? "");
        const nfce = await EmitNfceUseCase(nfceService, order, document);
        if (!!nfce.length) {
          await addOrderLog(order, "NF Emitida", undefined, undefined, nfce);
          return nfce;
        } else {
          await addOrderLog(order, "Falha ao emitir NF");
          GetError(
            new Error(t("order.nfce.emit_failed_verbose")),
            "emitNfce",
            "OrderProvider",
            t("order.nfce.emit_failed_verbose"),
            ErrorCodeType.NFCE
          );
        }
      } catch (error) {
        GetError(
          error,
          "emitNfce",
          "UseOrderProvider",
          t("order.nfce.emit_failed_verbose"),
          ErrorCodeType.NFCE
        );
        await addOrderLog(
          order,
          `Falha ao emitir nota fiscal - ${(error as any)?.response?.data?.message &&
          (error as any)?.response?.data?.message
          }`
        );
      }
    },
    [addOrderLog, kiosk, orderRepository, t]
  );

  const printNfce = useCallback(
    async (nfce: IEmitedNfce[]) => {
      try {
        if (nfce) {
          addOrdersToPrintQueue(CreatePrintNfceUseCase(nfce));
        }
      } catch (error) {
        GetError(
          error,
          "printNfce",
          "UseOrderProvider",
          t("order.nfce.print_failed"),
          ErrorCodeType.NFCE
        );
        toast(t("order.nfce.print_failed"), "error");
      }
    },
    [addOrdersToPrintQueue, toast, t]
  );

  const handleNfcePrintModal = useCallback(
    async (order: IOrder) => {
      try {
        try {
          if (kiosk?.mustPrintEletronicInvoice) {
            await printNfceRef.current?.open();
          }
          const documentSnapshot = docToNf.current;
          showLoading();
          const nfs = await emitNfce(order, documentSnapshot ?? undefined);
          if (nfs && nfs.length > 0 && kiosk?.mustPrintEletronicInvoice) {
            await printNfce(nfs);
          } else {
            if (kiosk?.mustPrintEletronicInvoice)
              toast(t("order.nfce.emit_failed_verbose"), "error");
          }
        } catch (_err) {
          emitNfce(order, docToNf.current ?? undefined);
        } finally {
          hideLoading();
          setDocumentToNf("");
          docToNf.current = null;
          setIncludDocument(false);
          setEnableNfAction(true);
          setDocError("");
        }
      } catch (error) {
        GetError(
          error,
          "handleNfcePrintModal",
          "UseOrderProvider",
          `${(error as any)?.message ?? t("order.nfce.emit_failed_verbose")}`,
          ErrorCodeType.NFCE
        );
      }
    },
    [
      emitNfce,
      hideLoading,
      printNfce,
      printNfceRef,
      showLoading,
      toast,
      kiosk,
      t
    ]
  );

  const getPassword = useCallback(
    async (_kiosk: Kiosk) => {
      showLoading();
      return await GetPasswordUseCase(
        orderRepository,
        _kiosk.localId,
        sessaoIdPos ?? ""
      );
    },
    [orderRepository, sessaoIdPos, showLoading]
  );

  const onSucessPayment = useCallback(
    async (
      _kiosk: Kiosk,
      orderWithCustomer: IOrder,
      transactionType: TransactionType,
      payment?: Payment,
      recharge?: RechargeType,
      pedidoPos?: IPedidoPosDTO
    ) => {
      if (orderWithCustomer.customer?.document || includDocument) {
        const newDocument = orderWithCustomer.customer?.document || documentToNf;
        setDocumentToNf(newDocument);
        setIncludDocument(true);
        docToNf.current = newDocument.replace(/\D/g, "") || null;
      } else {
        setIncludDocument(false);
        docToNf.current = null;
      }
      paymentReceipt.current = payment;
      _order.current = orderWithCustomer;
      const paidOrder = {
        ...orderWithCustomer,
        payment,
      };

      let password: IPasswordPainel | undefined = undefined;

      if (!recharge && _kiosk.mustEnableManagementPassword) {
        password = await getPassword(_kiosk);
      }
      const paidOrderWithPaymentApproved = { ...paidOrder, paymentApproved: true, passwordPanelCode: password?.passwordForPrinting };

      let syncResult: ISyncOrderResult | undefined;

      const isOfflineRechargeFlow =
        recharge === "Recharge" && orderWithCustomer.isOfflineRecharge;

      if (
        isOfflineRechargeFlow ||
        (transactionType !== TransactionType.cashless &&
          transactionType !== TransactionType.pospaid)
      ) {
        syncResult = await syncOrder(
          paidOrderWithPaymentApproved,
          transactionType,
          _kiosk,
          recharge
        );
      } else {
        syncResult = {
          pedidoPOS: pedidoPos,
          success: !!pedidoPos,
        };
      }
      await addOrderLog(
        paidOrderWithPaymentApproved,
        "Pagamento realizado",
        syncResult?.pedidoPOS,
        payment
      );

      if (!recharge && syncResult?.pedidoPOS) {
        const printOrders = CreatePrintOrderUseCase(
          syncResult.pedidoPOS,
          _kiosk
        );
        addOrderLog(
          {
            ...paidOrderWithPaymentApproved,
            printType: _kiosk.printType,
            printOrders,
          },
          "Enviado para impressão",
          syncResult.pedidoPOS
        );
        addOrdersToPrintQueue(printOrders);
        addOrdersToPrintQueue(
          CreatePrintRemoteUseCase(paidOrderWithPaymentApproved, syncResult.pedidoPOS, _kiosk)
        );
      } else if (!recharge && !syncResult?.pedidoPOS) {
        addLog("print", {
          message: "Print no started: missing pedidoPOS",
          orderLog: { paidOrderWithPaymentApproved },
        });
      }

      if (orderWithCustomer.totalValue > 0) {
        if (
          transactionType !== TransactionType.cashless &&
          transactionType !== TransactionType.pospaid &&
          transactionType !== TransactionType.pix //TODO: Remover ao criar comprovante pix
        ) {
          await handleReceiptPrintModal(_kiosk, transactionType);
        }

        const isTransaction =
          transactionType === TransactionType.credit ||
          transactionType === TransactionType.debit ||
          transactionType === TransactionType.pix ||
          transactionType === TransactionType.voucher;

        const isRecharge = recharge === "Recharge";

        if (
          _kiosk.generateEletronicInvoice &&
          ((!_kiosk.generateEletronicInvoiceForRecharge && !isRecharge) ||
            (_kiosk.generateEletronicInvoiceForRecharge && isTransaction))
        ) {
          if (recharge !== "WalletRecharge") {
            handleNfcePrintModal(paidOrder);
          }
        }
      }

      if (
        recharge === "Recharge" &&
        orderWithCustomer.isOfflineRecharge &&
        payment
      ) {
        addOrdersToPrintQueue(CreatePrintReceiptUseCase(payment));
      }

      if (
        false && //pedido salvo sem senha
        _kiosk.mustEnableManagementPassword &&
        !password?.passwordForPrinting &&
        !recharge
      ) {
        toastFullscreen(
          t("order.toast.no_password_warning"),
          "warning",
          {
            onClose: () => {
              replace("/");
            },
            time: 4000,
          }
        );
      } else if (!recharge) {
        let time = 4000;
        if (
          kiosk?.localId.toLowerCase() === "2c51e3b0-c4fa-e926-7238-398442b2b2d3" ||
          kiosk?.localId.toLowerCase() === "8D2DD6CE-72FC-90DA-6399-374D42D898B5") {
          time = 4000;
        }
        toastFullscreen(
          kiosk?.messageOnPayment ?? t("order.toast.success_default"),
          "success",
          {
            onClose: () => {
              replace("/");
            },
            time: time,
          }
        );
      }

      hideLoading();
      clearCart();
    },
    [
      addOrderLog,
      hideLoading,
      clearCart,
      getPassword,
      syncOrder,
      addOrdersToPrintQueue,
      addLog,
      handleReceiptPrintModal,
      handleNfcePrintModal,
      toastFullscreen,
      replace,
      kiosk,
      includDocument,
      documentToNf,
      t
    ]
  );

  const validateCart = useCallback(
    async (_order: IOrder) => {
      if (_order.orderItems.length === 0) {
        await addOrderLog(_order, t("order.errors.cart_empty"));
        throw GetError(
          new Error(t("order.errors.cart_empty")),
          "startPayment",
          "UseOrderProvider",
          t("order.errors.cart_empty"),
          ErrorCodeType.GENERAL
        );
      }
    },
    [addOrderLog, t]
  );

  const getCustomer = useCallback(
    async (_order: IOrder, _kiosk: Kiosk, _recharge?: RechargeType) => {
      if (_order.isOfflineRecharge) {
        return _order;
      }

      const hasItemOnDiscountList = _order.orderItems?.some(
        (item) => item.availableOnDiscountList === true
      );

      if (
        _kiosk?.ignoreCustomerIdentificationWithoutDiscountList &&
        !hasItemOnDiscountList && !_kiosk?.customerFieldEnable &&
        !_kiosk.prism &&
        _kiosk.enableCpfDiscount
      ) {
        return _order;
      }
      if (
        (_kiosk?.customerFieldEnable &&
          !_kiosk.customerFieldDisabled &&
          _kiosk.customerField.length) ||
        _kiosk?.generateEletronicInvoice ||
        _kiosk?.prism ||
        _kiosk?.enableCpfDiscount
      ) {
        const customer = await customerRef.current?.open(_kiosk, _recharge);
        _order = {
          ..._order,
          customer: {
            ..._order.customer,
            name: customer?.name ?? _order.customer?.name ?? "",
            phone: customer?.phone ?? _order.customer?.phone ?? "",
            document: customer?.document ?? _order.customer?.document ?? "",
            documentToNf: customer?.documentToNf ?? "",
            email: customer?.email ?? _order.customer?.email ?? "",
            clientIdentificator:
              customer?.clientIdentificator ??
              _order.customer?.clientIdentificator ??
              "",
            prism: customer?.prism ?? _order.customer?.prism ?? "",
          },
        };
      }
      return _order;
    },
    [customerRef]
  );

  const handlePaymentError = useCallback(
    async (error: unknown, order: IOrder): Promise<never> => {
      const iError = Array.isArray(error) ? error[0] : (error as IError);
      if (iError?.message?.includes("Pix")) {
        try {
          const cancelledPix: PixPendingTransaction = JSON.parse(
            iError.message
          );

          const paymentPix: PixApprovedTransaction = {
            ownerId: cancelledPix?.ownerId ?? "",
            paymentId: cancelledPix?.paymentId ?? "",
            qrCodeLink: cancelledPix?.qrCodeLink ?? "",
            status: cancelledPix?.status ?? "pending",
            value: cancelledPix?.value ?? order.totalValue,
            acquirerType: cancelledPix.acquirerType,
            createdAt: cancelledPix.createdAt,
            endToEndId: cancelledPix.endToEndId,
          };

          await addOrderLog(
            {
              ...order,
              paymentPix,
            },
            cancelledPix?.message ?? `[-003] ${t("order.errors.payment_failed")}`,
            undefined,
            {
              FinancialTransactionModel: {
                Status: StatusPedido.AguardandoPagamento,
              } as IFinancialTransactionModel,
            } as Payment
          );
        } catch (pixParseError) {
          const message =
            (pixParseError as IError)?.message ?? `[-004] ${t("order.errors.payment_failed")}`;
          await addOrderLog(order, message);
          toast(message, "error");
        } finally {
          throw GetError(
            error,
            "startPayment",
            "UseOrderProvider",
            t("order.errors.payment_failed"),
            ErrorCodeType.PAYMENT
          );
        }
      }

      let errMsg = "";
      try {
        const parsedError = JSON.parse(iError.message);
        errMsg = parsedError?.message ?? "";
      } catch {
        const rawMessage = (error as any)?.response?.data?.message;
        if (typeof rawMessage === "string") {
          const parts = rawMessage.split(" :");
          errMsg = parts.length > 1 ? parts[1] : rawMessage;
        } else {
          errMsg = iError.message;
        }
      }

      await addOrderLog(order, errMsg ?? `[-005] ${t("order.errors.payment_failed")}`);

      if (errMsg && !errMsg.includes("Fechado pelo usuário")) {
        toast(errMsg ?? t("order.errors.payment_failed"), "error");
      }

      throw GetError(
        error,
        "startPayment",
        "UseOrderProvider",
        t("order.errors.payment_failed"),
        ErrorCodeType.PAYMENT
      );
    },
    [addOrderLog, toast, t]
  );

  const startPayment = useCallback(
    async (
      order: IOrder,
      recharge?: RechargeType,
      paymentType?: TransactionType
    ) => {
      if (!kiosk) {
        const payment = {
          success: false,
          errorMessage: t("order.errors.kiosk_config_failed"),
        } as Payment;

        const paymentResult = {
          order,
          payment,
        } as IPaymentResult;

        await addOrderLog(
          order,
          t("order.errors.kiosk_config_failed"),
          undefined,
          payment
        );
        return paymentResult;
      }

      if (!paymentProvider.current) {
        const payment = {
          success: false,
          errorMessage: t("order.errors.payment_provider_init_failed"),
        } as Payment;

        const paymentResult = {
          order,
          payment,
        } as IPaymentResult;

        await addOrderLog(
          order,
          "Falha ao iniciar pagamento",
          undefined,
          payment
        );
        return paymentResult;
      }

      setRechargePayment(recharge);

      try {
        await validateCart(order);

        console.log("Validating inventory for order:", order);
        await validateInventory(order, kiosk);

        const orderWithCustomer = await getCustomer(order, kiosk, recharge);

        let orderToPay = { ...orderWithCustomer };

        if (kiosk?.enableCpfDiscount && orderWithCustomer.customer?.document && kiosk?.eventId) {
          const result = await customerDiscountsRef.current?.open(orderToPay, kiosk.eventId);
          orderToPay = result || orderToPay;
        }

        await addOrderLog(
          { ...orderToPay, printType: kiosk.printType },
          "Pagamento iniciado"
        );
        console.log("orderToPay", orderToPay);
        const result = await paymentProvider.current.open(
          orderToPay,
          isNominalData.current ? TransactionType.cashless : paymentType,
          isNominalData.current ? cardData ?? undefined : undefined
        );

        await onSucessPayment(
          kiosk,
          result.order,
          result.paymentType,
          result.payment,
          recharge,
          result.pedidoPos
        );

        return result;
      } catch (_error) {
        return handlePaymentError(_error, order);
      }
    },
    [
      addOrderLog,
      getCustomer,
      handlePaymentError,
      kiosk,
      onSucessPayment,
      validateCart,
      validateInventory,
      cardData,
      isNominalData,
      customerDiscountsRef,
      t,
    ]
  );

  useImperativeHandle(orderProviderRef, () => ({ startPayment }));

  return {
    startPayment,
    paymentProvider,
    rechargePayment,
    documentToNf,
    onpressKeyboard,
    onPressBack,
    includDocument,
    setIncludDocument,
    docError,
    enableNfAction,
    documentOwnerName,
    documentLookupLoading,
    t
  };
};

export default UseOrderProvider;
